import * as React from "react";
import { useMenuGroupNavigation } from "./useMenuGroupNavigation";

/**
 * Gets only top-level focusable elements (buttons, inputs) from a container,
 * excluding nested elements that are inside other focusable elements.
 *
 * @param container - The container element to search within
 * @returns Array of top-level focusable elements
 */
export const getTopLevelFocusableElements = (
  container: Element
): HTMLElement[] => {
  const candidates = container.querySelectorAll<HTMLElement>("button, input");

  const topLevelElements = Array.from(candidates).filter((element) => {
    let parent = element.parentElement;
    while (parent && parent !== container) {
      if (parent.tagName === "BUTTON" || parent.tagName === "INPUT") {
        return false;
      }
      parent = parent.parentElement;
    }
    return true;
  });

  return topLevelElements;
};

/**
 * Filters menu items to get only top-level items, excluding nested items inside custom menu items.
 * This ensures proper navigation order by filtering out menu items that are descendants of custom menu items.
 *
 * @param parentMenu - The parent menu container element
 * @returns Array of top-level menu items (HTMLElement[])
 */
export const getTopLevelMenuItems = (parentMenu: Element): HTMLElement[] => {
  const candidates = parentMenu.querySelectorAll('[role="menuitem"]');

  const items = Array.from(candidates).filter((el) => {
    let parent = el.parentElement;
    while (parent && parent !== parentMenu) {
      if (parent.getAttribute("role") === "menuitem") return false;
      parent = parent.parentElement;
    }

    return true;
  }) as HTMLElement[];

  return items;
};

interface BoundaryConditions {
  currentRow: number;
  currentCol: number;
  isLastRow: boolean;
  isFirstRow: boolean;
  isLastItem: boolean;
  isFirstItem: boolean;
}

interface GridInfo {
  totalColumns: number;
  totalButtons: number;
  rows: number;
}

/**
 * Custom hook for handling 4D navigation in menus with custom menu items.
 *
 * Key Navigation Features:
 * - Arrow keys: Navigate within the current menu group/section
 * - Tab: Move forward to the first item in the next menu group
 * - Shift+Tab: Move backward to the last item in the previous menu group
 *
 * Group Detection:
 * - Uses the new useMenuGroupNavigation hook for proper section detection
 * - Supports both regular menu items and custom grid items
 *
 * Accessibility:
 * - Follows WAI-ARIA menu navigation patterns
 * - Tab/Shift+Tab moves between menu sections (group-level navigation)
 * - Arrow keys handle within-section navigation
 * - Focus management ensures proper keyboard accessibility
 */
export const useCustomMenuNavigation = (gridInfo: GridInfo) => {
  const { totalColumns, totalButtons, rows } = gridInfo;
  const menuGroupNavigation = useMenuGroupNavigation();

  const getBoundaryConditions = React.useCallback(
    (focusedIndex: number): BoundaryConditions => {
      const currentRow = Math.floor(focusedIndex / totalColumns);
      const currentCol = focusedIndex % totalColumns;

      return {
        currentRow,
        currentCol,
        isLastRow: currentRow === rows - 1,
        isFirstRow: currentRow === 0,
        isLastItem: focusedIndex === totalButtons - 1,
        isFirstItem: focusedIndex === 0,
      };
    },
    [totalColumns, rows, totalButtons]
  );

  /**
   * Do custom navigation only if the key pressed is an arrow key or Tab.
   */
  const customKeyBoardNavigation = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>): boolean => {
      const navigationDirection = event.key;
      const isArrowKeyNavigation = navigationDirection.startsWith("Arrow");
      const isTabNavigation = navigationDirection === "Tab";
      return isTabNavigation || isArrowKeyNavigation;
    },
    []
  );

  const calculateTargetIndex = React.useCallback(
    (
      event: React.KeyboardEvent<HTMLDivElement>,
      currentCol: number,
      nextColumns: number,
      nextGridButtons: HTMLElement[]
    ): number => {
      switch (event.key) {
        case "ArrowDown":
          const targetCol = Math.min(currentCol, nextColumns - 1);
          return targetCol;
        case "ArrowUp":
          const nextRows = Math.ceil(nextGridButtons.length / nextColumns);
          const targetColUp = Math.min(currentCol, nextColumns - 1);
          const lastRowStartIndex = (nextRows - 1) * nextColumns;
          return Math.min(
            lastRowStartIndex + targetColUp,
            nextGridButtons.length - 1
          );
        case "ArrowLeft":
          return nextGridButtons.length - 1;
        case "ArrowRight":
          return 0;
        default:
          return 0;
      }
    },
    []
  );

  // Helper function to find next valid menu item (skipping empty custom grids)
  const findValidMenuItem = React.useCallback(
    (
      startIndex: number,
      allMenuItems: HTMLElement[],
      direction: "forward" | "backward"
    ): HTMLElement | null => {
      let menuItem = null;
      let searchIndex = startIndex;
      let attempts = 0;
      const maxAttempts = allMenuItems.length;

      while (attempts < maxAttempts) {
        const candidateMenuItem = allMenuItems[searchIndex];
        if (!candidateMenuItem) break;

        const isCustomGrid =
          candidateMenuItem.hasAttribute("data-grid-columns");

        if (isCustomGrid) {
          const items = getTopLevelFocusableElements(candidateMenuItem);
          if (items.length !== 0) {
            menuItem = candidateMenuItem;
            break;
          }
        } else {
          menuItem = candidateMenuItem;
          break;
        }

        attempts++;
        if (direction === "forward") {
          searchIndex = (searchIndex + 1) % allMenuItems.length;
        } else {
          searchIndex =
            (searchIndex - 1 + allMenuItems.length) % allMenuItems.length;
        }
      }

      return menuItem;
    },
    []
  );

  const handleMenuItemNavigation = React.useCallback(
    (
      event: React.KeyboardEvent<HTMLDivElement>,
      currentCustomMenuItem: HTMLElement,
      boundaries: BoundaryConditions
    ): void => {
      const parentMenu = currentCustomMenuItem.closest('[role="menu"]');
      if (!parentMenu) return;

      const items = getTopLevelMenuItems(parentMenu);

      const currentMenuItemIndex = items.indexOf(currentCustomMenuItem);

      if (currentMenuItemIndex === -1) return;

      let nextMenuItemIndex = currentMenuItemIndex;
      switch (event.key) {
        case "ArrowDown":
        case "ArrowRight":
          nextMenuItemIndex = currentMenuItemIndex + 1;
          break;
        case "ArrowUp":
        case "ArrowLeft":
          nextMenuItemIndex = currentMenuItemIndex - 1;
          break;
        case "Tab":
          nextMenuItemIndex = event.shiftKey
            ? currentMenuItemIndex - 1
            : currentMenuItemIndex + 1;
          break;
      }

      if (nextMenuItemIndex >= items.length) {
        nextMenuItemIndex = 0;
      } else if (nextMenuItemIndex < 0) {
        nextMenuItemIndex = items.length - 1;
      }

      // Find the next valid menu item (skip empty custom grids)
      const direction =
        event.key === "ArrowDown" || event.key === "ArrowRight"
          ? "forward"
          : "backward";
      const validMenuItem = findValidMenuItem(
        nextMenuItemIndex,
        items,
        direction
      );

      if (!validMenuItem) return;

      const isNextItemCustom = validMenuItem.hasAttribute("data-grid-columns");

      if (isNextItemCustom) {
        const topLevelElements = getTopLevelFocusableElements(validMenuItem);

        if (topLevelElements.length > 0) {
          const firstElement = topLevelElements[0];
          const isFirstElementInput = firstElement.tagName === "INPUT";

          if (isFirstElementInput) {
            firstElement.focus();
          } else {
            const gridButtons = topLevelElements;

            if (gridButtons.length > 0) {
              const nextGridColumns = parseInt(
                validMenuItem.getAttribute("data-grid-columns") || "1",
                10
              );

              const targetIndex = calculateTargetIndex(
                event,
                boundaries.currentCol,
                nextGridColumns,
                Array.from(gridButtons)
              );

              if (gridButtons[targetIndex]) {
                gridButtons[targetIndex].focus();
              } else {
                gridButtons[0].focus();
              }
            } else {
              firstElement.focus();
            }
          }
        }
      } else {
        /** Next item is not custom menu but normal AppMenuItem */
        validMenuItem.focus();
      }
    },
    [calculateTargetIndex, findValidMenuItem]
  );

  // Main navigation handler that dispatches to group or regular navigation
  const handleItemNavigation = React.useCallback(
    (
      event: React.KeyboardEvent<HTMLDivElement>,
      currentCustomMenuItem: HTMLElement,
      boundaries: BoundaryConditions
    ): void => {
      // Check if this is Tab or Shift+Tab - use the new group navigation
      if (event.key === "Tab") {
        const handled = menuGroupNavigation.handleKeyDown(event);
        if (handled) {
          return;
        }
      }
      handleMenuItemNavigation(event, currentCustomMenuItem, boundaries);
    },
    [menuGroupNavigation, handleMenuItemNavigation]
  );

  /**
   * Main keyboard event handler for custom menu items with 4D grid navigation.
   *
   * @param event - The keyboard event from the custom menu item
   *
   * **Triggers custom navigation when:**
   * - Arrow keys pressed at grid boundaries (first/last row/column/item)
   * - Tab key pressed anywhere in the grid (for group navigation)
   *
   * **Allows default behavior for:**
   * - Arrow keys within grid boundaries (for galleries/color grids)
   * - Text input behavior within search boxes
   * - Escape key
   * - All other keys (Enter, Space, etc.)
   */
  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>): void => {
      const currentElement = event.target as HTMLElement;

      const currentCustomMenuItem = currentElement.closest<HTMLElement>(
        "[data-grid-columns]"
      );
      if (!currentCustomMenuItem) return;

      const gridButtons =
        currentCustomMenuItem.querySelectorAll<HTMLElement>("button");
      const focusedIndex = Array.from(gridButtons).indexOf(currentElement);
      const isGridButtonFocused = focusedIndex !== -1;

      const boundaries = isGridButtonFocused
        ? getBoundaryConditions(focusedIndex)
        : {
            currentRow: 0,
            currentCol: 0,
            isLastRow: true,
            isFirstRow: true,
            isLastItem: true,
            isFirstItem: true,
          };

      const isCustomNavigation = customKeyBoardNavigation(event);

      if (isCustomNavigation) {
        event.preventDefault();
        event.stopPropagation();
        if (currentCustomMenuItem) {
          handleItemNavigation(event, currentCustomMenuItem, boundaries);
        }
      }
    },
    [getBoundaryConditions, customKeyBoardNavigation, handleItemNavigation]
  );

  return {
    handleKeyDown,
    getBoundaryConditions,
    customKeyBoardNavigation,
    calculateTargetIndex,
    handleItemNavigation,
  };
};
